import { Box, FormControlLabel, InputLabel, Typography } from '@mui/material';
import { DIFFERENT_COLOR, TYPE_ANALYTICS_MAP_VIEW } from 'common/defines/constants';
import { changeSizeWaterUptakeFieldList, mapViewSelector, setDataWaterUptakeField } from 'store/slices/mapViewSlice';
import { get, isEmpty } from 'lodash';
import {
  getSizeWaterUptakeFieldAnalytic,
  getWaterUptakeFieldAnalytic,
} from 'services/analytics/apiAnalyticsConfig.services';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useMemo, useState } from 'react';

import InfoTable from '../CropCover/InfoTable';
import PieChartCommon from 'components/Common/PieChartCommon';
import { QUERY_KEY } from 'constants/constants';
import SkeletonCharts from 'components/Common/SkeletonCharts';
import { SwipeableViewsCustom } from '../../SwipeableViewsCustom';
import { SwitchCustom } from 'components/Common/SwitchCustom';
import WaterUptakeFieldCardLayer from '../../Layer/WaterUptakeField';
import randomColor from 'randomcolor';
import { useClientSettingsStyle } from 'components/Dashboard/ClientSettings/ClientSettingsStyle';
import { useQuery } from 'react-query';

const WaterUptakeFieldAnalytics = () => {
  const dispatch = useAppDispatch();
  const [chartMode, setChartMode] = useState(false);
  const classes = useClientSettingsStyle();

  const { analyticId, sizeWaterUptakeFieldSelected, dataWaterUptakeField, sizeWaterUptakeFieldList } =
    useAppSelector(mapViewSelector);

  useQuery(
    [QUERY_KEY.WATER_UPTAKE_FIELD_ANALYTIC, analyticId],
    () => getSizeWaterUptakeFieldAnalytic(analyticId || ''),
    {
      enabled: !!analyticId && isEmpty(sizeWaterUptakeFieldList),
      onSuccess: (response) => {
        if (response.data) {
          dispatch(changeSizeWaterUptakeFieldList(response.data));
        }
      },
    }
  );

  const { data: dataCropType } = useQuery(
    [QUERY_KEY.SIZE_WATER_UPTAKE_FIELD_ANALYTIC, analyticId, sizeWaterUptakeFieldSelected],
    () => getWaterUptakeFieldAnalytic(analyticId || '', sizeWaterUptakeFieldSelected + ''),
    { enabled: !!analyticId && !!sizeWaterUptakeFieldSelected }
  );

  const dataPie = useMemo(() => {
    if (dataWaterUptakeField) {
      return dataWaterUptakeField.map((_item: any) => ({
        name: _item.type,
        value: _item.lduseArea,
        totalTree: _item.totalTree,
      }));
    }
    return [];
  }, [dataWaterUptakeField]);

  const getDataChart = useMemo(() => {
    return dataWaterUptakeField?.map((_item: any) => ({
      name: _item.type,
      value: chartMode ? _item.totalTree : _item.lduseArea,
    }));
  }, [chartMode, dataWaterUptakeField]);

  const hasTreeCount = !!(dataWaterUptakeField?.length && dataWaterUptakeField.some((item: any) => item.totalTree > 0))

  const colorWaterUptakeField = useMemo(() => {
    if (dataCropType?.data) {
      let colors: string[] = [];
      dispatch(
        setDataWaterUptakeField(
          dataCropType.data.map((item: any, index: number) => {
            const colorRandom = randomColor({ luminosity: 'dark' });
            const _color = item.color || get(DIFFERENT_COLOR, `[${index}]`, colorRandom);
            colors.push(_color);
            return {
              ...item,
              visible: true,
              color: _color,
            };
          })
        )
      );
      return colors;
    }
    return [];
  }, [dataCropType, dispatch]);

  return (
    <SwipeableViewsCustom
      isDisableLabel
      analyticName={TYPE_ANALYTICS_MAP_VIEW.WATER_UPTAKE_FIELD}
      analyticTab={
        <>
          {dataWaterUptakeField?.length > 0 ? (
            <>
              <PieChartCommon title="Water Uptake Field" dataChart={getDataChart} colorCharts={colorWaterUptakeField} />
              {hasTreeCount && (
                <>
                  <InputLabel sx={{ marginLeft: '25px', fontSize: '14px' }}>Chart Mode:</InputLabel>
                  <FormControlLabel
                    sx={{ marginLeft: '30px' }}
                    control={
                      <SwitchCustom
                        checked={chartMode}
                        onClick={() => {
                          setChartMode(!chartMode);
                        }}
                      />
                    }
                    label={<Typography className={classes.textSetting}>{chartMode ? 'Tree Count' : 'Area'}</Typography>}
                  />
                </>
              )}
              <Box component={'div'} sx={{ marginTop: '30px' }}>
                <InfoTable dataPie={dataPie} hasTreeCount={hasTreeCount} />
              </Box>
            </>
          ) : (
            <>
              <SkeletonCharts count={1} />
            </>
          )}
        </>
      }
      layerTab={<WaterUptakeFieldCardLayer labelCard={TYPE_ANALYTICS_MAP_VIEW.WATER_UPTAKE_FIELD} />}
      isShowDownloadButton={dataWaterUptakeField?.length > 0}
    />
  );
};

export default WaterUptakeFieldAnalytics;
